import { EditorState, convertFromRaw } from 'draft-js';
import { createSelector } from 'reselect';
import { VenueFcf } from '@classpass/protobuf-definitions/lib/web/models/inventory/venue_config';

import _keyBy from 'lodash/keyBy';
import { parseDateInTimezone } from 'helpers/intl-fns';
import { FCF_MODE } from 'constants/logic';
import { venueDetails, venuePageAdditionalPhotos, waivers, venueVideos } from 'store/saga/entities/bound';
import { selectRouteParams } from 'selectors/general';

export const selectIsFcfModeSelectionVisible = createSelector(
  state => state.venuePage?.fcfModeSelection?.isFeatureFlagEnabled,
  state => state.venuePage?.fcfModeSelection?.canShowFcfSection,
  (isFeatureFlagEnabled, userCanSeeFcfSection) => (isFeatureFlagEnabled && userCanSeeFcfSection) || false
);

export const selectCanBulkUpdateFcfMode = createSelector(
  state => state.venuePage?.fcfModeSelection?.canBulkUpdate,
  canBulkUpdate => canBulkUpdate || false
);

export const selectEligibleVenuesForFcfBulkUpdate = createSelector(
  state => state.venuePage?.fcfModeSelection?.eligibleManagedVenues,
  eligibleManagedVenues => eligibleManagedVenues || []
);

export const selectCurrentVenueFcfMode = createSelector(
  state => state.venuePage?.fcfModeSelection?.currentVenueFcfMode,
  currentVenueFcfMode => currentVenueFcfMode || VenueFcf.VENUE_FCF_UNSPECIFIED
);

export const selectVenuePageAdditionalPhotos = (state, venueId) =>
  venuePageAdditionalPhotos.loaded(state, venueId) ? venuePageAdditionalPhotos.data(state, venueId) : [];
export const selectVenuePageAdditionalPhotosIsLoading = (state, venueId) =>
  venuePageAdditionalPhotos.loading(state, venueId);

export const selectActiveTab = createSelector(selectRouteParams, routeParams => routeParams.tab || 'profile');

export const selectVenueDetails = createSelector(
  (state, venueId) => venueDetails.select(state, venueId),
  (state, venueId) => {
    const key = venueVideos.defaultKey(null, venueVideos.method.options, venueId);
    return venueVideos.select(state, key);
  },
  ({ data, error, loading, loaded }, videosData) => {
    const { attributes, ...restData } = data || {};
    const { data: videos, loading: videosLoading, loaded: videosLoaded } = videosData;
    const isLoading = loading && videosLoading;
    const isLoaded = loaded && videosLoaded;

    return {
      error,
      isLoading,
      isLoaded,
      data: !isLoaded
        ? {}
        : {
            ...restData,
            safety: attributes ? _keyBy(attributes, 'attributeName') : null,
            videos,
            fcfMode: FCF_MODE.ONE_PER_VENUE,
            fcfModeApplyToAllVenues: false,
          },
    };
  }
);

export const selectLatestVenueWaiver = createSelector(
  (state, venueId) => waivers.select(state, waivers.defaultKey(null, waivers.method.latest, venueId)),
  ({ data, error, loading, loaded }) => {
    const { waiver: waiverResponse } = data || {};
    const { isEnabled, waiverId } = waiverResponse || {};
    const createdOn =
      waiverResponse?.waiverVersion?.createdOn && parseDateInTimezone(waiverResponse.waiverVersion.createdOn, 'UTC');

    let waiver;
    let waiverVersionId;
    if (waiverResponse?.waiverVersion?.waiverData) {
      try {
        waiverVersionId = waiverResponse.waiverVersion.waiverVersionId;
        waiver = EditorState.createWithContent(convertFromRaw(JSON.parse(waiverResponse.waiverVersion.waiverData)));
      } catch (err) {
        // Errror parsing json, ignore
      }
    }

    return {
      error,
      isLoading: loading,
      isLoaded: loaded,
      data: {
        isEnabled,
        waiver,
        waiverId,
        waiverVersionId,
        createdOn,
      },
    };
  }
);
